import { ref } from 'vue'

import { apiService } from '@/services/apiService'
import { connectivityService } from '@/services/connectivityService'
import { logService, LOG_CATEGORIES } from '@/services/logService'
import { isTauri } from '@/services/platformService'
import { showSuccess } from '@/services/toastService'

const MAINTENANCE_CHECK_INTERVAL_MS = 30_000
const MAINTENANCE_RELOAD_STORAGE_KEY = 'routecamp:maintenance-reload-completed'

type MaintenanceCheckReason = 'setup' | 'interval' | 'visibility' | 'window-focus' | 'manual'

class MaintenanceModeService {
  readonly isActive = ref(false)
  readonly hasChecked = ref(false)

  private isSetup = false
  private isChecking = false
  private intervalId: ReturnType<typeof setInterval> | null = null
  private updateToastShown = false
  private visibilityHandler: (() => void) | null = null
  private windowFocusHandler: (() => void) | null = null

  setup(): void {
    if (this.isSetup) return
    this.isSetup = true

    // The native (Tauri) apps are offline-first — used view-only, with the local library and track
    // recording, on week-long off-grid trips. A backend deployment (which flips the API's
    // maintenance flag) must never black them out with the maintenance overlay, so skip the checks
    // entirely: no initial check, no interval. Web keeps the full maintenance flow.
    if (isTauri()) {
      logService.info(LOG_CATEGORIES.GENERAL, 'Skipping maintenance checks on Tauri')
      return
    }

    void this.checkStatus('setup')

    this.intervalId = setInterval(() => {
      if (this.hasWindowFocus()) {
        void this.checkStatus('interval')
      }
    }, MAINTENANCE_CHECK_INTERVAL_MS)

    this.visibilityHandler = () => {
      if (document.visibilityState === 'visible' && this.hasWindowFocus()) {
        void this.checkStatus('visibility')
      }
    }
    document.addEventListener('visibilitychange', this.visibilityHandler)

    this.windowFocusHandler = () => {
      void this.checkStatus('window-focus')
    }
    window.addEventListener('focus', this.windowFocusHandler)
  }

  async checkStatus(reason: MaintenanceCheckReason = 'manual'): Promise<boolean | null> {
    // Never activate maintenance mode on the native apps — see setup(). Guarding here (the only
    // place `isActive` is set true) keeps the guarantee regardless of the caller.
    if (isTauri()) return null
    if (this.isChecking || !connectivityService.isEffectivelyOnline.value) {
      return null
    }

    this.isChecking = true
    const wasActive = this.isActive.value

    try {
      const status = await apiService.getMaintenanceStatus()
      this.hasChecked.value = true
      this.isActive.value = status.active

      if (!status.active && wasActive) {
        this.markReloadAfterMaintenance()
        this.reloadPage()
      }

      return status.active
    } catch (error) {
      logService.warn(LOG_CATEGORIES.API, 'Failed to check maintenance status', {
        reason,
        error,
      })
      return null
    } finally {
      this.isChecking = false
    }
  }

  resetForTests(): void {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId)
      this.intervalId = null
    }
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler)
      this.visibilityHandler = null
    }
    if (this.windowFocusHandler) {
      window.removeEventListener('focus', this.windowFocusHandler)
      this.windowFocusHandler = null
    }
    this.isSetup = false
    this.isChecking = false
    this.updateToastShown = false
    this.isActive.value = false
    this.hasChecked.value = false
    this.removeReloadAfterMaintenanceMarker()
  }

  showPendingUpdateToast(): void {
    if (this.updateToastShown || !this.hasReloadAfterMaintenanceMarker()) return

    this.updateToastShown = true
    this.removeReloadAfterMaintenanceMarker()

    const releaseLabel = this.getReleaseLabel()
    const message = releaseLabel
      ? `Updated to version ${releaseLabel}. routecamp is ready again.`
      : 'Update installed. routecamp is ready again.'

    showSuccess(message, 8000)
  }

  private hasWindowFocus(): boolean {
    if (document.visibilityState === 'hidden') return false
    return typeof document.hasFocus !== 'function' || document.hasFocus()
  }

  private markReloadAfterMaintenance(): void {
    try {
      sessionStorage.setItem(MAINTENANCE_RELOAD_STORAGE_KEY, '1')
    } catch (error) {
      logService.warn(LOG_CATEGORIES.GENERAL, 'Failed to persist maintenance reload marker', {
        error,
      })
    }
  }

  private hasReloadAfterMaintenanceMarker(): boolean {
    try {
      return sessionStorage.getItem(MAINTENANCE_RELOAD_STORAGE_KEY) === '1'
    } catch {
      return false
    }
  }

  private removeReloadAfterMaintenanceMarker(): void {
    try {
      sessionStorage.removeItem(MAINTENANCE_RELOAD_STORAGE_KEY)
    } catch {
      // Ignore unavailable session storage.
    }
  }

  private getReleaseLabel(): string | null {
    const tag = document
      .querySelector<HTMLMetaElement>('meta[name="routecamp-release-tag"]')
      ?.content.trim()
    if (tag) return tag

    const commit = document
      .querySelector<HTMLMetaElement>('meta[name="routecamp-release-commit"]')
      ?.content.trim()
    return commit || null
  }

  private reloadPage(): void {
    window.location.reload()
  }
}

export const maintenanceModeService = new MaintenanceModeService()
